import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import {
  fetchConversations,
  fetchConversationMessages,
  sendConversationMessage,
} from "../api/api";
import { avatarColor, avatarInitials, formatActivityDateLabel, formatActivityTime } from "../utils/activityAvatar";

import "../App.css";

const PAGE_SIZE = 5;

function formatDate(iso) {
  return new Date(iso).toLocaleString();
}

function Messaging() {

  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState("");
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]); // newest-first
  const [threadHasMore, setThreadHasMore] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [directMessage, setDirectMessage] = useState("");
  const [sending, setSending] = useState(false);

  async function loadConversations() {
    setConversationsLoading(true);
    setConversationsError("");
    try {
      const data = await fetchConversations();
      setConversations(data);
    } catch (err) {
      setConversationsError(err.response?.data?.detail || "Could not load conversations.");
    } finally {
      setConversationsLoading(false);
    }
  }

  useEffect(() => {
    loadConversations();
  }, []);

  async function openConversation(conv) {
    setSelectedConvId(conv.id);
    setThreadLoading(true);
    try {
      const page = await fetchConversationMessages(conv.id, 0, PAGE_SIZE);
      setThreadMessages(page);
      setThreadHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setConversationsError(err.response?.data?.detail || "Could not load this conversation.");
    } finally {
      setThreadLoading(false);
    }
  }

  async function handleLoadMoreThread() {
    if (!selectedConvId) return;
    try {
      const page = await fetchConversationMessages(selectedConvId, threadMessages.length, PAGE_SIZE);
      setThreadMessages((prev) => [...prev, ...page]);
      setThreadHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setConversationsError(err.response?.data?.detail || "Could not load more messages.");
    }
  }

  async function handleSendDirect() {
    if (!directMessage.trim() || !selectedConvId) return;
    setSending(true);
    try {
      const msg = await sendConversationMessage(selectedConvId, directMessage.trim());
      setThreadMessages((prev) => [msg, ...prev]);
      setDirectMessage("");
      loadConversations();
    } catch (err) {
      setConversationsError(err.response?.data?.detail || "Could not send message.");
    } finally {
      setSending(false);
    }
  }

  const selectedConv = conversations.find((c) => c.id === selectedConvId);

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div className="messaging-container">

          <h1 className="messaging-title" style={{ marginTop: 0 }}>
            Messaging
          </h1>

          <p style={{ color: "#666", marginTop: "-8px" }}>
            One-to-one conversations with customers. Looking to message everyone at once?
            Use <Link to="/estate-updates">Estate Updates</Link> to broadcast — it also sends
            a push notification to every customer's phone.
          </p>

          {conversationsError && (
            <p className="login-error" style={{ color: "red" }}>{conversationsError}</p>
          )}

          <div className="recent-card">
            <h3>CONVERSATIONS</h3>

            {conversationsLoading && <p>Loading…</p>}
            {!conversationsLoading && conversations.length === 0 && <p>No conversations yet.</p>}

            {conversations.map((conv) => (
              <div
                key={conv.id}
                className="broadcast-item"
                style={{ cursor: "pointer", background: conv.id === selectedConvId ? "#f3f4f6" : undefined }}
                onClick={() => openConversation(conv)}
              >
                <div>
                  <h4>{conv.customer_name || `Customer #${conv.user_id}`}</h4>
                  <p>
                    {conv.customer_mobile ? `${conv.customer_mobile} • ` : ""}
                    {conv.last_message_body
                      ? `${conv.last_message_body.slice(0, 60)}${conv.last_message_body.length > 60 ? "…" : ""}`
                      : "No messages yet"}
                    {conv.last_message_at ? ` • ${formatDate(conv.last_message_at)}` : ""}
                  </p>
                </div>

                {conv.unread_count > 0 && (
                  <div className="new-tag">{conv.unread_count} new</div>
                )}
              </div>
            ))}
          </div>

          {selectedConvId && (
            <div className="broadcast-card">
              <h2>{selectedConv?.customer_name || `Customer #${selectedConv?.user_id}`}</h2>

              {threadLoading && <p>Loading thread…</p>}

              {!threadLoading && threadMessages.map((msg, i) => {
                const isStaff = msg.sender_id === user?.id;
                const sender = isStaff ? "You (Admin)" : (selectedConv?.customer_name || "Customer");
                const dateLabel = formatActivityDateLabel(msg.created_at);
                const prevDateLabel = i > 0 ? formatActivityDateLabel(threadMessages[i - 1].created_at) : null;
                return (
                  <React.Fragment key={msg.id}>
                    {dateLabel !== prevDateLabel && <div className="activity-date-header">{dateLabel}</div>}
                    <div className="activity-row">
                      <div className="activity-avatar" style={{ background: avatarColor(isStaff) }}>
                        {avatarInitials(sender)}
                      </div>
                      <div className="activity-body-col">
                        <div className="activity-meta">
                          {formatActivityTime(msg.created_at)} <span className="activity-sender">{sender}</span>
                        </div>
                        <p className="activity-text">{msg.body}</p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {threadHasMore && (
                <button className="send-btn" onClick={handleLoadMoreThread}>
                  Load more
                </button>
              )}

              <div className="message-box" style={{ marginTop: "1rem" }}>
                <label>Reply</label>
                <textarea
                  rows="4"
                  value={directMessage}
                  onChange={(e) => setDirectMessage(e.target.value)}
                  placeholder="Type your message..."
                />
              </div>

              <div className="send-section">
                <button className="send-btn" onClick={handleSendDirect} disabled={sending || !directMessage.trim()}>
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </div>
            </div>
          )}

        </div>

      </div>

    </div>

  );

}

export default Messaging;
